import { Instagram, Facebook, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";
import logoWhite from "@/assets/logo-white.svg";
import { Badge } from "@/components/ui/badge";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const menuLinks = [
    { label: "Tickets", href: "/tickets", disabled: false },
    { label: "Guide", href: "/guide", disabled: false },
    { label: "Lifestyle", href: "/lifestyle", disabled: false },
    { label: "Travel", href: "/travel", disabled: false },
    { label: "Merch", href: "#", disabled: true, comingSoon: true },
  ];

  const supportLinks = [
    { label: "FAQs", href: "/faq", disabled: false },
    { label: "Contact", href: "/contact", disabled: false },
    { label: "Accessibility", href: "#", disabled: true },
    { label: "Press", href: "#", disabled: true },
  ];

  return (
    <footer className="bg-yacht-cream px-4 py-8 md:px-8 md:py-12">
      {/* Giant pill container */}
      <div className="relative rounded-[3rem] md:rounded-[4rem] overflow-hidden border-4 bg-yacht-blue border-yacht-seaglass">
        {/* Noise texture overlay */}
        <div 
          className="absolute inset-0 opacity-[0.1] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Boats graphic background - large and faded */}
        <div 
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `url(${boatsGraphic})`,
            backgroundSize: '100%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Main content */}
        <div className="relative z-10 px-8 py-12 md:px-16 md:py-20">
          {/* Top section - 4 columns */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8">
            {/* Brand column */}
            <div className="md:col-span-1">
              <img src={logoWhite} alt="Yacht Rock Detroit" className="h-20 mb-6 opacity-90" style={{ filter: 'sepia(0.3) saturate(0.5) brightness(1.1) hue-rotate(-10deg)' }} />
              <p className="text-yacht-cream/70 text-sm leading-relaxed">
                Detroit's smoothest summer festival. Golden sunsets, smooth vibes — see you on the river, summer 2027.
              </p>
            </div>

            {/* Stay Connected */}
            <div>
              <h4 className="text-yacht-cream font-semibold text-sm tracking-wide uppercase mb-5">
                Stay Connected
              </h4>
              <div className="flex items-center gap-3">
                <a 
                  href="https://www.instagram.com/yachtrockdetroit/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-yacht-cream/10 hover:bg-yacht-yellow hover:text-yacht-charcoal text-yacht-cream flex items-center justify-center transition-all duration-200"
                  aria-label="Follow Yacht Rock Detroit on Instagram"
                >
                  <span className="sr-only">Follow Yacht Rock Detroit on Instagram</span>
                  <Instagram size={18} />
                </a>
                <a 
                  href="https://www.facebook.com/p/Yacht-Rock-Detroit-61574424432230/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-yacht-cream/10 hover:bg-yacht-yellow hover:text-yacht-charcoal text-yacht-cream flex items-center justify-center transition-all duration-200"
                  aria-label="Follow Yacht Rock Detroit on Facebook"
                >
                  <span className="sr-only">Follow Yacht Rock Detroit on Facebook</span>
                  <Facebook size={18} />
                </a>
                <a 
                  href="mailto:info@roostertail.com"
                  className="w-10 h-10 rounded-full bg-yacht-cream/10 hover:bg-yacht-yellow hover:text-yacht-charcoal text-yacht-cream flex items-center justify-center transition-all duration-200"
                  aria-label="Email Yacht Rock Detroit"
                >
                  <span className="sr-only">Email Yacht Rock Detroit</span>
                  <Mail size={18} />
                </a>
              </div>
            </div>

            {/* Menu */}
            <div>
              <h4 className="text-yacht-cream font-semibold text-sm tracking-wide uppercase mb-5">
                Menu
              </h4>
              <nav className="flex flex-col gap-3">
                {menuLinks.map((link) => (
                  link.disabled ? (
                    <span
                      key={link.label}
                      className="text-yacht-cream/30 text-sm cursor-not-allowed flex items-center gap-2"
                    >
                      {link.label}
                      {link.comingSoon && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-yacht-cream/30 text-yacht-cream/40 uppercase tracking-wider">
                          Coming Soon
                        </Badge>
                      )}
                    </span>
                  ) : (
                    <Link
                      key={link.label}
                      to={link.href}
                      className="text-yacht-cream/70 hover:text-yacht-yellow transition-colors duration-200 text-sm"
                    >
                      {link.label}
                    </Link>
                  )
                ))}
              </nav>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-yacht-cream font-semibold text-sm tracking-wide uppercase mb-5">
                Support
              </h4>
              <nav className="flex flex-col gap-3">
                {supportLinks.map((link) => (
                  link.disabled ? (
                    <span
                      key={link.label}
                      className="text-yacht-cream/30 text-sm cursor-not-allowed"
                    >
                      {link.label}
                    </span>
                  ) : (
                    <Link
                      key={link.label}
                      to={link.href}
                      className="text-yacht-cream/70 hover:text-yacht-yellow transition-colors duration-200 text-sm"
                    >
                      {link.label}
                    </Link>
                  )
                ))}
              </nav>
            </div>
          </div>

          {/* Divider */}
          <div className="my-12 h-px bg-yacht-cream/10" />

          {/* Bottom section */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Copyright & links */}
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 text-yacht-cream/50 text-xs">
              <span>©{currentYear} Yacht Rock Detroit. All rights reserved.</span>
              <div className="flex gap-6">
                <span className="text-yacht-cream/30 cursor-not-allowed">Privacy Policy</span>
                <Link to="/terms-and-conditions" className="text-yacht-cream/50 hover:text-yacht-yellow transition-colors duration-200">Terms</Link>
              </div>
            </div>

            {/* Social repeat for bottom */}
            <div className="flex items-center gap-2">
              <a 
                href="https://www.instagram.com/yachtrockdetroit/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-yacht-cream/50 hover:text-yacht-yellow transition-colors"
                aria-label="Follow Yacht Rock Detroit on Instagram"
              >
                <span className="sr-only">Follow Yacht Rock Detroit on Instagram</span>
                <Instagram size={16} />
              </a>
              <a 
                href="https://www.facebook.com/p/Yacht-Rock-Detroit-61574424432230/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-yacht-cream/50 hover:text-yacht-yellow transition-colors"
                aria-label="Follow Yacht Rock Detroit on Facebook"
              >
                <span className="sr-only">Follow Yacht Rock Detroit on Facebook</span>
                <Facebook size={16} />
              </a>
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
